import { PixelIcon } from "../components/PixelArt";
import { useBountyUI, useProjectsUI } from "../features";
import { useCallback, useState } from "preact/hooks";
import { api } from "../api";
import { jobSizeLabel } from "../../shared/projects.ts";
import { describeCadence, formatDateTime } from "../format";
import { href } from "../router";
import type { Schedule } from "../../shared/types";
import { useLive } from "../live";
import { recallSchedules, rememberSchedules } from "../board-cache";
import { Empty, ErrorBox, Spinner, TabBar } from "../components/Shell";
import { AwardToast } from "../components/AwardToast";
import { takeLastNotice } from "../award";
import { scheduleCardLabel } from "../a11y-labels.ts";

/**
 * A schedule card: the anchor wraps only the thumbnail and body (the tappable
 * area that opens the schedule form); an active schedule gets a sibling
 * actions row below it with "Done early" (`completeScheduleEarly`, see
 * server/db.ts), which posts the next occurrence as already done so the
 * regular materialization pass skips it when the time comes.
 */
// "Done early" (complete the next occurrence now, api.schedules.completeEarly) is off the card for now
// (owner, 2026-09-24): its button took most of the screen. The server route and CSS stay for a
// better-placed version.
function ScheduleCard({ s }: { s: Schedule }) {
  const bountyUIEnabled = useBountyUI();
  // Projects (docs/projects.md): with projectsUI on a big job reads "Project".
  const jobSize = jobSizeLabel(useProjectsUI());
  const cadence = describeCadence(s);
  const bigJob = bountyUIEnabled && !!s.big_job;
  const nextLine = s.active ? (s.next_due_at ? `Next: ${formatDateTime(s.next_due_at)}` : "") : "Paused";

  return (
    <>
      <div class={`card task-card ${s.active ? "" : "inactive"}`}>
        {/* Named by its title and its lines, so the photo placeholder is not read first. */}
        <a class="schedule-link" href={href({ name: "schedule", id: s.id })} aria-label={scheduleCardLabel(s.title, [cadence, bigJob && jobSize, nextLine])}>
          <div class="thumb" aria-hidden="true">
            {s.start_photo_id ? <img src={api.photos.thumbUrl(s.start_photo_id)} alt="" loading="lazy" /> : <span class="thumb-empty">No photo</span>}
          </div>
          <div class="task-card-body">
            <div class="task-title">{s.title}</div>
            <div class="task-meta">{cadence}</div>
            {bigJob && <div class="task-meta job-size">{jobSize}</div>}
            <div class="task-meta">{nextLine}</div>
          </div>
        </a>
      </div>
    </>
  );
}

export function Schedules() {
  // Painted from the list shown last (src/board-cache.ts) and refreshed behind it, like the boards;
  // a list from memory gets no entrance animation.
  const [items, setItems] = useState<Schedule[] | null>(() => recallSchedules());
  const [error, setError] = useState<string | null>(null);
  // "Schedule added. First task Mon, Oct 5, 9:00 AM." after a create, as the board says "Task added".
  const [notice, setNotice] = useState(() => takeLastNotice());
  const dismissNotice = useCallback(() => setNotice(null), []);

  const reload = useLive(
    async (initial) => {
      try {
        const list = await api.schedules.list();
        rememberSchedules(list);
        setItems(list);
        setError(null);
      } catch (e) {
        if (initial) setError(e instanceof Error ? e.message : "Could not load");
      }
    },
    [],
  );

  return (
    <>
      {/* Fixed to the window, outside the sliding scroller, as on the board. */}
      {notice !== null && <AwardToast gold={null} text={notice} onDismiss={dismissNotice} />}
      <main class="content content-wide card-grid">
        {error && <ErrorBox message={error} onRetry={reload} />}
        {!error && items === null && <Spinner />}
        {items && items.length === 0 && <Empty>No repeating tasks yet. Add one with the + button.</Empty>}
        {items && items.map((s) => <ScheduleCard key={s.id} s={s} />)}
      </main>
      <TabBar current="schedules">
        <a class="fab" href={href({ name: "newSchedule" })} aria-label="Add schedule">
          <PixelIcon name="add" />
        </a>
      </TabBar>
    </>
  );
}
